<template>
  <div data-testid="layers-container-ref" class="overflow-hidden">
    <EditorDeckSlidesThreeDDrillHoleLayer
      v-for="hole in layers"
      :key="hole.title"
      :hole="hole"
      :has-c-s-v-files="hole.files.length > 0"
      :selected-layers="selectedLayers"
      @set-layer-visible="handleSetVisible"
      @toggle-collapsed="toggleCollapsed"
      @click.left="handleClick"
      @click.right="clickIfNotSelected($event, hole)"
      @contextmenu.stop.prevent="handleContextmenu($event, hole)"
    >
      <template v-for="file in (hole as unknown as DrillLayer).files" :key="file.fileId">
        <div :class="{ 'max-h-0 overflow-hidden': collapsed.includes(hole.title) }">
          <EditorDeckSlidesThreeDLayerListItem
            v-for="label in file.labels"
            :key="label.id"
            :selected-in-panel="isSelected(label)"
            :visible="label.visible"
            class="group h-9"
            @set-visible="handleSetVisible($event, label)"
            @click.left="handleClick($event, label)"
            @click.right="clickIfNotSelected($event, label)"
            @contextmenu.stop.prevent="handleContextmenu($event, label)"
          >
            <div class="flex items-center flex-1 w-full truncate layer-title ml-10 pl-5">
              <PhotoIcon v-if="label.clusterId" class="flex-shrink-0 w-3 h-3 mr-2 text-copper-200" />
              <TypeIcon v-else class="flex-shrink-0 w-3 h-3 mr-2 text-copper-200" data-testid="label-type-icon" />
              <span :title="label.title" class="truncate max-w-4/5">{{ label.title }}</span>
            </div>
          </EditorDeckSlidesThreeDLayerListItem>
        </div>
      </template>
    </EditorDeckSlidesThreeDDrillHoleLayer>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref, computed } from 'vue'
import { PhotoIcon } from '@heroicons/vue/24/outline'

import TypeIcon from '@/components/icons/TypeIcon.svg'
import type { SceneElement } from '@/entities/model3d'
import type { LabelsManager } from '~/lib/three-d-resources/labels-manager'
import { selectableId, isDrillLayer } from '~/lib/three-d-editor/utils'
import type { DrillLabelLayer, DrillLayer, DrillCSVFile } from '~/entities/drillLabel'
import type { ThreeDElement } from '@/composables/useLayerSelect'
import type { Option } from '~/components/VDropdown.vue'

const props = defineProps({
  layers: {
    type: Array as PropType<DrillLayer[]>,
    required: true,
  },
  selectedLayers: {
    type: Array as PropType<DrillLabelLayer[]>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  labelsManager: {
    type: Object as PropType<LabelsManager>,
    required: true,
  },
  scopingElement: {
    type: Object as PropType<SceneElement | null>,
    default: null,
  },
  selectedLabelId: {
    type: String as PropType<string | null>,
    default: null,
  },
  selectedLegend: {
    type: Object as PropType<Option | null>,
    default: undefined,
  },
  type: {
    type: String as PropType<string | null>,
    default: null,
  },
})

const emit = defineEmits(['set-layer-visible', 'click', 'contextmenu'])

const collapsed = ref<(string | number)[]>([])

const selectedLayerIds = computed(() => props.selectedLayers.map(selectableId))

function isSelected(layer: DrillLabelLayer | DrillLayer): boolean {
  return props.selectedLayers.some(l => selectableId(l) === selectableId(layer))
}

function handleClick(e: MouseEvent, layer: DrillLabelLayer | DrillLayer) {
  emit('click', e, layer)
}

function clickIfNotSelected(e: MouseEvent, layer: DrillLabelLayer | DrillLayer): void {
  if (!isSelected(layer)) {
    emit('click', e, layer)
  }
}

function toggleCollapsed(id: string | number) {
  collapsed.value = collapsed.value.includes(id) ? collapsed.value.filter(cid => cid !== id) : [...collapsed.value, id]
}

function handleContextmenu(e: Event, layer: DrillLabelLayer | DrillLayer) {
  if (isDrillLayer(layer)) {
    emit('contextmenu', e, layer)
    return
  }
  emit('contextmenu', e, getSelectionFor(layer))
}

function handleSetVisible(visible: boolean, layer: DrillLabelLayer | DrillLayer) {
  if (isDrillLayer(layer) && layer.type === 'cluster') {
    emit('set-layer-visible', [{ clusterId: Number(layer.id), dataSetId: layer.dataSetId, visible }])
  } else if ('dataSetId' in layer) {
    emit('set-layer-visible', [{ dataSetId: layer.dataSetId, visible }])
  } else {
    emit('set-layer-visible', getAllLabels(layer, visible))
  }
}

function getSelectionFor(layer: DrillLabelLayer): ThreeDElement | ThreeDElement[] {
  const prevSelectedLayers = selectedLayerIds.value
  const layerSelectableId = selectableId(layer)
  const wasPreviouslySelected = prevSelectedLayers.includes(layerSelectableId)

  if (prevSelectedLayers.length > 1 && wasPreviouslySelected) {
    return props.selectedLayers
  }

  return layer
}

function getAllLabels(drillHole: DrillLayer | DrillCSVFile | DrillLabelLayer, visible: boolean) {
  let drillLabels: (DrillLabelLayer | DrillLayer)[] = []

  if ('files' in drillHole) {
    drillLabels = drillHole.files.length ? drillHole.files.flatMap((x: DrillCSVFile) => x.labels) : [drillHole]
  } else if ('labels' in drillHole) {
    drillLabels = drillHole.labels
  } else {
    drillLabels = [drillHole]
  }

  return drillLabels.map((x: DrillLabelLayer | DrillLayer) => ({ drillLabelId: x.id, visible }))
}
</script>
